<template>
  <div class="product-list">
    <van-nav-bar
      :title="categoryTitle"
      left-arrow
      @click-left="onClickLeft"
      :border="false"
      class="custom-nav"
    />

    <!-- 搜索框 -->
    <div class="search-bar">
      <van-search
        v-model="searchKeyword"
        placeholder="搜索商品"
        shape="round"
        @search="handleSearch"
        :background="transparent"
      />
    </div>

    <!-- 子分类选项卡 -->
    <div class="sub-category-wrapper">
      <van-sidebar v-model="activeCategory" class="custom-sidebar">
        <van-sidebar-item 
          v-for="category in subCategories" 
          :key="category.id"
          :title="category.name"
          @click="handleCategoryClick(category.id)"
        />
      </van-sidebar>

      <div class="product-content">
        <!-- 商品数量显示 -->
        <div class="result-info" v-if="!loading && products.length > 0">
          <span class="count">{{ total }}</span>
          <span class="count-text">件商品</span>
          <span class="search-tip" v-if="searchKeyword">
            <span class="divider">|</span>
            {{ searchKeyword }}
          </span>
        </div>

        <!-- 产品列表 -->
        <van-pull-refresh v-model="refreshing" @refresh="onRefresh" success-text="刷新成功">
          <van-list
            v-model:loading="listLoading"
            :finished="finished"
            finished-text="没有更多了"
            @load="onLoad"
          >
            <van-grid :column-num="2" :gutter="6">
              <van-grid-item
                v-for="product in products"
                :key="product.id"
                @click="goToDetail(product.id)"
              >
                <div class="product-card">
                  <van-image
                    :src="product.imgUrl"
                    width="100%"
                    fit="contain"
                    radius="4"
                    class="product-image"
                  >
                    <template #loading>
                      <div class="loading-placeholder">
                        <van-loading type="spinner" size="20" color="#c62f2f" />
                      </div>
                    </template>
                    <template #error>
                      <div class="error-placeholder">
                        <van-icon name="photo-fail" size="20" />
                        <span>加载失败</span>
                      </div>
                    </template>
                  </van-image>
                  <div class="product-info">
                    <div class="product-name">{{ product.name }}</div>
                  </div>
                </div>
              </van-grid-item>
            </van-grid>
          </van-list>
        </van-pull-refresh>

        <!-- 空状态 -->
        <van-empty
          v-if="!loading && products.length === 0"
          description="暂无商品"
          image="search"
        />

        <!-- 加载状态 -->
        <div class="loading-container" v-if="loading">
          <van-loading type="spinner" color="#c62f2f" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import request from '@/utils/request';

const router = useRouter();
const route = useRoute();
const loading = ref(false);
const listLoading = ref(false);
const finished = ref(false);
const refreshing = ref(false);
const activeCategory = ref(0);
const products = ref([]);
const total = ref(0);
const subCategories = ref([]);
const searchKeyword = ref('');
const currentCategoryId = ref('');
const pageSize = ref(10);
const currentPage = ref(1);
const isFirstLoad = ref(true);

// 获取分类标题
const categoryTitle = ref('商品列表');

// 获取子分类
const fetchSubCategories = async () => {
  try {
    const response = await request.get('/api/v1/cinnabarOpen/selectAllSpecificCategories', {
      majorCategoryId: route.params.categoryId
    });
    if (response.code === '00000' && response.data) {
      subCategories.value = [
        { id: 'all', name: '全部' },
        ...response.data
      ];
    }
  } catch (error) {
    console.error('获取子分类失败:', error);
  }
};

// 获取商品列表
const fetchProducts = async (specificCategoryId = '', isLoadMore = false) => {
  if (!isLoadMore) {
    loading.value = true;
  }
  try {
    const requestBody = {
      majorCategoriesId: route.params.categoryId,
      ...(specificCategoryId && specificCategoryId !== 'all' ? { specificCategoriesId: specificCategoryId } : {}),
      ...(searchKeyword.value ? { name: searchKeyword.value } : {}),
      pageSize: pageSize.value,
      pageNum: currentPage.value
    };
    const response = await request.post('/api/v1/cinnabarOpen/selectAllProduct', requestBody);
    if (response.code === '00000' && response.data) {
      const { records, total: totalCount } = response.data;
      if (isLoadMore) {
        products.value = [...products.value, ...records];
      } else {
        products.value = records;
      }
      total.value = totalCount;
      
      // 判断是否加载完所有数据
      finished.value = products.value.length >= totalCount;
      
      // 第一次加载后，重置标志
      if (isFirstLoad.value) {
        isFirstLoad.value = false;
      }
    }
  } catch (error) {
    console.error('获取商品列表失败:', error);
  } finally {
    loading.value = false;
    listLoading.value = false;
  }
};

// 获取主分类名称
const fetchCategoryTitle = async () => {
  try {
    const response = await request.get('/api/v1/cinnabarOpen/selectAllMajorCategories');
    if (response.code === '00000' && response.data) {
      const category = response.data.find(item => item.id === route.params.categoryId);
      if (category) {
        categoryTitle.value = category.name;
      }
    }
  } catch (error) {
    console.error('获取分类名称失败:', error);
  }
};

// 下拉刷新
const onRefresh = async () => {
  finished.value = false;
  currentPage.value = 1;
  isFirstLoad.value = true;
  await fetchProducts(currentCategoryId.value);
  refreshing.value = false;
};

// 上拉加载
const onLoad = () => {
  // 如果是第一次加载，不执行加载更多
  if (isFirstLoad.value) {
    listLoading.value = false;
    return;
  }
  currentPage.value += 1;
  fetchProducts(currentCategoryId.value, true);
};

// 处理搜索
const handleSearch = () => {
  finished.value = false;
  currentPage.value = 1;
  isFirstLoad.value = true;
  fetchProducts(currentCategoryId.value);
};

// 处理子分类点击
const handleCategoryClick = (categoryId) => {
  currentCategoryId.value = categoryId;
  finished.value = false;
  currentPage.value = 1;
  isFirstLoad.value = true;
  fetchProducts(categoryId);
};

// 返回上一页
const onClickLeft = () => {
  router.back();
};

// 跳转到商品详情
const goToDetail = (id) => {
  router.push({
    name: 'productDetail',
    params: { id }
  });
};

onMounted(async () => {
  await fetchCategoryTitle();
  await fetchSubCategories();
  // 初始加载所有商品
  fetchProducts();
});
</script>

<style scoped>
.product-list {
  min-height: 100vh;
  background-color: #f7f8fa;
}

.custom-nav {
  background-color: transparent;
  backdrop-filter: blur(10px);
}

:deep(.van-nav-bar) {
  background-color: rgba(255, 255, 255, 0.95);
}

:deep(.van-nav-bar__title) {
  font-weight: 600;
  color: #333;
}

.search-bar {
  position: sticky;
  top: 46px;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

:deep(.van-search) {
  padding: 8px 12px;
}

:deep(.van-search__content) {
  background-color: #f5f5f5;
}

.sub-category-wrapper {
  display: flex;
  height: calc(100vh - 46px - 54px);
}

.custom-sidebar {
  border-right: 1px solid rgba(0, 0, 0, 0.05);
}

:deep(.van-sidebar) {
  width: 70px;
  height: 100%;
  background-color: #fff;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #ddd transparent;
}

:deep(.van-sidebar)::-webkit-scrollbar {
  width: 4px;
}

:deep(.van-sidebar)::-webkit-scrollbar-thumb {
  background-color: #ddd;
  border-radius: 2px;
}

:deep(.van-sidebar-item) {
  padding: 12px 6px;
  font-size: 13.5px;
  text-align: center;
  transition: all 0.3s ease;
}

:deep(.van-sidebar-item--select) {
  color: #c62f2f;
  border-color: #c62f2f;
  font-weight: 600;
  background-color: #fff1f1;
}

.result-info {
  background-color: transparent;
  padding: 8px 12px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
}

.count {
  color: #c62f2f;
  font-weight: 600;
  margin-right: 2px;
}

.count-text {
  color: #666;
}

.search-tip {
  color: #999;
  font-size: 12px;
}

.search-tip .divider {
  margin: 0 8px;
  color: #ddd;
}

.product-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 4px;
  background-color: #f7f8fa;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.product-content::-webkit-scrollbar {
  display: none;
}

.product-card {
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease;
}

.product-card:active {
  transform: scale(0.98);
}

.product-image {
  width: 100%;
  aspect-ratio: 1;
  display: block;
  background-color: #fff;
}

.loading-placeholder,
.error-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f8f8f8;
  color: #999;
  font-size: 12px;
  gap: 8px;
}

.product-info {
  padding: 6px 4px;
}

.product-name {
  font-size: 12px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

:deep(.van-pull-refresh) {
  overflow: visible;
}
</style>
